import { IAddItemBase } from "@interfaces";

/**
 * What the visitor was doing when a social sign-in took them off the site.
 *
 * Apple (and Google) sign-in is a full-page redirect, so the panel that was
 * holding the selected fares is destroyed. Without this, someone who pressed
 * Book Now and signed in with Apple came back signed in but empty-handed: the
 * add never happened, and the intent to go to the checkout was lost with it.
 *
 * Session storage rather than local: the journey is one tab, and an intent that
 * outlived the tab would add a product to some later, unrelated visit.
 */
const PENDING_KEY = "pendingSocialAction";
const ADDED_TO_CART_KEY = "showAddedToCartAfterSignIn";

export type PendingSocialMode = "addToCart" | "bookNow";

export interface IPendingSocialAction {
    mode: PendingSocialMode;
    /** The exact payload the panel would have posted. */
    item: IAddItemBase;
    /** Where to come back to, for the Add to Cart case. */
    returnPath: string;
}

export const storePendingSocialAction = (action: IPendingSocialAction) => {
    try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(action));
    } catch {
        // Storage refusing us costs the hand-off, not the sign-in.
    }
};

export const readPendingSocialAction = (): IPendingSocialAction | null => {
    try {
        const raw = sessionStorage.getItem(PENDING_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Anything not recognisable is treated as absent rather than trusted:
        // this drives an add to the visitor's cart.
        if (!parsed?.item?.tdmsProductId) return null;
        if (parsed.mode !== "addToCart" && parsed.mode !== "bookNow") return null;
        return parsed as IPendingSocialAction;
    } catch {
        return null;
    }
};

export const clearPendingSocialAction = () => {
    try {
        sessionStorage.removeItem(PENDING_KEY);
    } catch {
        // See above.
    }
};

/**
 * Raised when an Add to Cart completed after the visitor came back from a social
 * sign-in, so the panel can show its "go to cart or keep shopping" prompt — the
 * one they would have seen had they never left the page.
 */
export const flagAddedToCartAfterSignIn = () => {
    try {
        sessionStorage.setItem(ADDED_TO_CART_KEY, "true");
    } catch {
        // The item is in the cart either way; only the prompt is lost.
    }
};

export const consumeAddedToCartAfterSignIn = (): boolean => {
    try {
        const flagged = sessionStorage.getItem(ADDED_TO_CART_KEY) === "true";
        if (flagged) sessionStorage.removeItem(ADDED_TO_CART_KEY);
        return flagged;
    } catch {
        return false;
    }
};
